import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

import { useAddons } from '../hooks/useAddons';
import {
  VisualisationArtifact,
  VisualisationArtifactSourceReference,
  VisualisationArtifactState,
  VisualisationChartBuilderRequest,
  VisualisationChartBuilderResponse,
} from '../types/addons';
import {
  dataObjectApi,
  DataObjectDto,
  DataObjectInsightsDto,
} from '../utils/dataObjectApi';
import { visualisationArtifactApi, VISUALISATION_ADDON_ID } from '../utils/addonApi';
import { CHART_RENDERING_AVAILABLE } from '../addons/chartRendering';

type ChipColor = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';

const STATE_COLOR: Record<VisualisationArtifactState, ChipColor> = {
  ACTIVE: 'info',
  PUBLISHED: 'success',
  SHARED: 'primary',
  ARCHIVED: 'warning',
  REMOVED: 'default',
};

const ARTIFACT_TYPE_LABELS: Record<string, string> = {
  'visualisation.chart.v1': 'Chart',
  'visualisation.template.v1': 'Template',
};

const CHART_TYPES: Array<{ value: VisualisationChartBuilderRequest['chartType']; label: string }> = [
  { value: 'bar', label: 'Bar' },
  { value: 'line', label: 'Line' },
  { value: 'table', label: 'Table' },
];

const AGGREGATIONS: Array<{ value: NonNullable<VisualisationChartBuilderRequest['aggregation']>; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'count', label: 'Count' },
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' },
];

type BuilderState = {
  name: string;
  description: string;
  chartType: VisualisationChartBuilderRequest['chartType'];
  dataObjectId: string;
  categoryField: string;
  valueField: string;
  seriesField: string;
  aggregation: NonNullable<VisualisationChartBuilderRequest['aggregation']>;
};

type PreviewResponse = {
  rows?: Record<string, unknown>[];
};

const formatDateTime = (value?: string): string => {
  if (!value) {
    return 'Not recorded';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const artifactTypeLabel = (artifactType: string): string =>
  ARTIFACT_TYPE_LABELS[artifactType] || artifactType;

const stateColor = (state: string): ChipColor =>
  STATE_COLOR[state as VisualisationArtifactState] || 'default';

const sourceReferences = (
  references: VisualisationArtifact['sourceReferences']
): VisualisationArtifactSourceReference[] => {
  if (!Array.isArray(references)) {
    return [];
  }
  return references.filter(
    (reference): reference is VisualisationArtifactSourceReference =>
      Boolean(reference) && typeof reference === 'object' && !Array.isArray(reference)
  );
};

const payloadSummary = (artifact: VisualisationArtifact): Array<[string, string]> => {
  const payload = artifact.payload || {};
  const rows: Array<[string, string]> = [];

  if (typeof payload.chartType === 'string') {
    rows.push(['Chart type', payload.chartType]);
  }
  if (typeof payload.builderContractVersion === 'string') {
    rows.push(['Builder contract', payload.builderContractVersion]);
  }
  if (typeof payload.aggregation === 'string') {
    rows.push(['Aggregation', payload.aggregation]);
  }
  if (typeof payload.dataObjectId === 'string') {
    rows.push(['Data object', payload.dataObjectId]);
  }

  const encoding =
    payload.encoding && typeof payload.encoding === 'object' && !Array.isArray(payload.encoding)
      ? (payload.encoding as Record<string, unknown>)
      : null;
  if (encoding) {
    ['categoryField', 'valueField', 'seriesField'].forEach(field => {
      if (typeof encoding[field] === 'string') {
        rows.push([field, String(encoding[field])]);
      }
    });
  }

  const topLevelKeys = Object.keys(payload);
  rows.push(['Payload keys', topLevelKeys.length === 0 ? 'None' : topLevelKeys.join(', ')]);
  return rows;
};

const errorMessage = (err: any, fallback: string): string =>
  err?.response?.data?.message || err?.message || fallback;

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Stack spacing={0.25}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
      {value}
    </Typography>
  </Stack>
);

const Visualiser: React.FC = () => {
  const { visualisationAddon, visualisationEnabled, refreshAddons } = useAddons();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigationState = location.state as { dataObjectId?: string; dataObjectName?: string } | null;
  const preselectedDataObjectId = searchParams.get('dataObjectId') || navigationState?.dataObjectId || '';
  const [artifacts, setArtifacts] = useState<VisualisationArtifact[]>([]);
  const [artifactsLoading, setArtifactsLoading] = useState(false);
  const [artifactsError, setArtifactsError] = useState<string | null>(null);
  const [selectedArtifactId, setSelectedArtifactId] = useState<string | null>(null);
  const [selectedArtifact, setSelectedArtifact] = useState<VisualisationArtifact | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [dataObjects, setDataObjects] = useState<DataObjectDto[]>([]);
  const [dataObjectsLoading, setDataObjectsLoading] = useState(false);
  const [dataObjectsError, setDataObjectsError] = useState<string | null>(null);
  const [selectedInsights, setSelectedInsights] = useState<DataObjectInsightsDto | null>(null);
  const [previewRows, setPreviewRows] = useState<Record<string, unknown>[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [builder, setBuilder] = useState<BuilderState>({
    name: navigationState?.dataObjectName ? `${navigationState.dataObjectName} chart` : '',
    description: '',
    chartType: 'bar',
    dataObjectId: preselectedDataObjectId,
    categoryField: '',
    valueField: '',
    seriesField: '',
    aggregation: 'none',
  });
  const [builderValidation, setBuilderValidation] = useState<string | null>(null);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [buildSuccess, setBuildSuccess] = useState<VisualisationChartBuilderResponse | null>(null);
  const [buildSubmitting, setBuildSubmitting] = useState(false);

  const loadArtifacts = useMemo(
    () => async () => {
      setArtifactsLoading(true);
      setArtifactsError(null);
      try {
        const response = await visualisationArtifactApi.list();
        const visibleArtifacts = response.filter(artifact => artifact.state !== 'REMOVED');
        setArtifacts(visibleArtifacts);
        setSelectedArtifactId(current =>
          current && visibleArtifacts.some(artifact => artifact.id === current)
            ? current
            : visibleArtifacts[0]?.id || null
        );
      } catch (err: any) {
        setArtifacts([]);
        setSelectedArtifactId(null);
        setSelectedArtifact(null);
        setArtifactsError(
          err?.response?.data?.message || err?.message || 'Failed to load visualisation artifacts'
        );
      } finally {
        setArtifactsLoading(false);
      }
    },
    []
  );

  const loadDataObjects = useMemo(
    () => async () => {
      setDataObjectsLoading(true);
      setDataObjectsError(null);
      try {
        const response = await dataObjectApi.get<DataObjectDto[]>('/api/v1/data-objects');
        setDataObjects(Array.isArray(response) ? response : []);
      } catch (err: any) {
        setDataObjects([]);
        setDataObjectsError(errorMessage(err, 'Failed to load Data Objects'));
      } finally {
        setDataObjectsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!visualisationEnabled) {
      return;
    }
    loadArtifacts();
    if (CHART_RENDERING_AVAILABLE) {
      loadDataObjects();
    }
  }, [loadArtifacts, loadDataObjects, visualisationEnabled]);

  useEffect(() => {
    if (!visualisationEnabled || !selectedArtifactId) {
      setSelectedArtifact(null);
      setDetailError(null);
      return;
    }

    let active = true;
    const loadDetail = async () => {
      setDetailLoading(true);
      setDetailError(null);
      try {
        const response = await visualisationArtifactApi.get(selectedArtifactId);
        if (active) {
          setSelectedArtifact(response);
        }
      } catch (err: any) {
        if (active) {
          setSelectedArtifact(null);
          setDetailError(
            err?.response?.data?.message || err?.message || 'Artifact detail is unavailable'
          );
        }
      } finally {
        if (active) {
          setDetailLoading(false);
        }
      }
    };

    loadDetail();
    return () => {
      active = false;
    };
  }, [selectedArtifactId, visualisationEnabled]);

  const selectedDataObject = useMemo(
    () => dataObjects.find(dataObject => dataObject.id === builder.dataObjectId) || null,
    [builder.dataObjectId, dataObjects]
  );

  useEffect(() => {
    if (!CHART_RENDERING_AVAILABLE || !visualisationEnabled || !builder.dataObjectId) {
      setSelectedInsights(null);
      setPreviewRows([]);
      setPreviewError(null);
      setPreviewLoading(false);
      return;
    }

    let active = true;
    const loadPreview = async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const [insights, preview] = await Promise.all([
          dataObjectApi.get<DataObjectInsightsDto>(
            `/api/v1/data-objects/${builder.dataObjectId}/insights`
          ),
          dataObjectApi.get<PreviewResponse>(
            `/api/v1/data-objects/${builder.dataObjectId}/preview?limit=25`
          ),
        ]);
        if (active) {
          setSelectedInsights(insights);
          setPreviewRows(Array.isArray(preview?.rows) ? preview.rows.slice(0, 25) : []);
        }
      } catch (err: any) {
        if (active) {
          setSelectedInsights(null);
          setPreviewRows([]);
          setPreviewError(errorMessage(err, 'Failed to load Data Object preview'));
        }
      } finally {
        if (active) {
          setPreviewLoading(false);
        }
      }
    };

    loadPreview();
    return () => {
      active = false;
    };
  }, [builder.dataObjectId, visualisationEnabled]);

  const previewFields = useMemo(() => {
    if (previewRows.length === 0) {
      return [] as string[];
    }
    return Object.keys(previewRows[0]);
  }, [previewRows]);

  useEffect(() => {
    if (previewFields.length === 0) {
      return;
    }
    setBuilder(current => {
      const nextCategory = current.categoryField || previewFields[0] || '';
      const defaultValue = previewFields.find(field => field !== nextCategory) || previewFields[0] || '';
      return {
        ...current,
        categoryField: nextCategory,
        valueField: current.valueField || defaultValue,
      };
    });
  }, [previewFields]);

  useEffect(() => {
    if (!selectedDataObject || builder.name.trim()) {
      return;
    }
    setBuilder(current => ({
      ...current,
      name: current.name.trim() ? current.name : `${selectedDataObject.name} chart`,
    }));
  }, [builder.name, selectedDataObject]);

  const updateBuilder = <K extends keyof BuilderState>(key: K, value: BuilderState[K]) => {
    setBuilder(current => ({ ...current, [key]: value }));
    setBuilderValidation(null);
    setBuildError(null);
  };

  const updateBuilderDataObject = (dataObjectId: string) => {
    setBuilder(current => ({
      ...current,
      dataObjectId,
      categoryField: '',
      valueField: '',
      seriesField: '',
    }));
    setBuilderValidation(null);
    setBuildError(null);
    setBuildSuccess(null);
  };

  const validateBuilder = (): string | null => {
    if (!builder.dataObjectId) {
      return 'Select a Data Object before building a chart.';
    }
    if (!builder.name.trim()) {
      return 'Enter a chart name.';
    }
    if (!builder.categoryField.trim()) {
      return builder.chartType === 'table'
        ? 'Choose the table category field.'
        : 'Choose the category field.';
    }
    if (builder.chartType !== 'table' && !builder.valueField.trim()) {
      return 'Choose the value field for bar and line charts.';
    }
    return null;
  };

  const handleBuildChart = async () => {
    const validation = validateBuilder();
    if (validation) {
      setBuilderValidation(validation);
      return;
    }

    setBuildSubmitting(true);
    setBuilderValidation(null);
    setBuildError(null);
    setBuildSuccess(null);
    try {
      const response = await visualisationArtifactApi.buildChart({
        name: builder.name.trim(),
        description: builder.description.trim() || null,
        chartType: builder.chartType,
        dataObjectId: builder.dataObjectId,
        categoryField: builder.categoryField.trim(),
        valueField: builder.chartType === 'table' ? null : builder.valueField.trim(),
        seriesField: builder.seriesField.trim() || null,
        aggregation: builder.aggregation,
      });
      setBuildSuccess(response);
      setSelectedArtifactId(response.artifact.id);
      setSelectedArtifact(response.artifact);
      setArtifacts(current => [
        response.artifact,
        ...current.filter(artifact => artifact.id !== response.artifact.id),
      ]);
      await loadArtifacts();
      setSelectedArtifactId(response.artifact.id);
      setSelectedArtifact(response.artifact);
    } catch (err: any) {
      setBuildError(errorMessage(err, 'Failed to build visualisation chart'));
    } finally {
      setBuildSubmitting(false);
    }
  };

  if (!visualisationEnabled) {
    return <Navigate to="/" replace />;
  }

  const selectedListArtifact = artifacts.find(artifact => artifact.id === selectedArtifactId);
  const detailArtifact = selectedArtifact || selectedListArtifact || null;
  const isTabularCompatible =
    !selectedInsights?.compatibleDataContracts
    || selectedInsights.compatibleDataContracts.includes('tabular.dataset.v1');
  const widgetArtifactId = buildSuccess?.dashboardWidget?.queryConfig?.artifactId;

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            Visualisation Studio
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
            <Chip label={visualisationAddon?.id || VISUALISATION_ADDON_ID} size="small" />
            <Chip label={`v${visualisationAddon?.version || 'unknown'}`} size="small" variant="outlined" />
            <Chip label={visualisationAddon?.state || 'ENABLED'} size="small" color="success" variant="outlined" />
          </Stack>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => {
              refreshAddons();
              loadArtifacts();
            }}
            variant="outlined"
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        {CHART_RENDERING_AVAILABLE ? (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography variant="h6">Chart builder</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Build a published Visualisation artifact from a readable Data Object and reuse the returned widget request in dashboards.
                    </Typography>
                  </Box>
                  {preselectedDataObjectId && (
                    <Chip label="Data Object preselected" size="small" color="info" variant="outlined" />
                  )}
                </Stack>

                {dataObjectsError && <Alert severity="warning">{dataObjectsError}</Alert>}
                {previewError && <Alert severity="warning">{previewError}</Alert>}
                {!isTabularCompatible && (
                  <Alert severity="warning">
                    This Data Object does not advertise the tabular.dataset.v1 contract required by Visualisation Studio.
                  </Alert>
                )}
                {builderValidation && <Alert severity="warning">{builderValidation}</Alert>}
                {buildError && <Alert severity="error">{buildError}</Alert>}
                {buildSuccess && (
                  <Alert severity="success">
                    Created {buildSuccess.artifact.name}. Widget request: {buildSuccess.dashboardWidget.title} ({buildSuccess.dashboardWidget.widgetType}) using {buildSuccess.dashboardWidget.querySourceType}
                    {widgetArtifactId ? ` artifact ${widgetArtifactId}` : ''}.
                  </Alert>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="visualisation-data-object-label">Data Object</InputLabel>
                      <Select
                        labelId="visualisation-data-object-label"
                        label="Data Object"
                        value={builder.dataObjectId}
                        disabled={dataObjectsLoading}
                        onChange={(event) =>
                          updateBuilderDataObject(event.target.value)
                        }
                      >
                        <MenuItem value="">
                          <em>Select Data Object</em>
                        </MenuItem>
                        {builder.dataObjectId
                          && !dataObjects.some(dataObject => dataObject.id === builder.dataObjectId) && (
                            <MenuItem value={builder.dataObjectId}>
                              {navigationState?.dataObjectName || builder.dataObjectId}
                            </MenuItem>
                        )}
                        {dataObjects.map(dataObject => (
                          <MenuItem key={dataObject.id} value={dataObject.id}>
                            {dataObject.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Chart name"
                      value={builder.name}
                      onChange={(event) => updateBuilder('name', event.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Description"
                      value={builder.description}
                      onChange={(event) => updateBuilder('description', event.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="visualisation-chart-type-label">Chart type</InputLabel>
                      <Select
                        labelId="visualisation-chart-type-label"
                        label="Chart type"
                        value={builder.chartType}
                        onChange={(event) =>
                          updateBuilder(
                            'chartType',
                            event.target.value as VisualisationChartBuilderRequest['chartType']
                          )
                        }
                      >
                        {CHART_TYPES.map(chartType => (
                          <MenuItem key={chartType.value} value={chartType.value}>
                            {chartType.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="visualisation-category-label">
                        {builder.chartType === 'table' ? 'Table field' : 'Category field'}
                      </InputLabel>
                      <Select
                        labelId="visualisation-category-label"
                        label={builder.chartType === 'table' ? 'Table field' : 'Category field'}
                        value={builder.categoryField}
                        disabled={previewLoading || previewFields.length === 0}
                        onChange={(event) => updateBuilder('categoryField', event.target.value)}
                      >
                        {previewFields.map(field => (
                          <MenuItem key={field} value={field}>
                            {field}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small" disabled={builder.chartType === 'table'}>
                      <InputLabel id="visualisation-value-label">Value field</InputLabel>
                      <Select
                        labelId="visualisation-value-label"
                        label="Value field"
                        value={builder.valueField}
                        disabled={builder.chartType === 'table' || previewLoading || previewFields.length === 0}
                        onChange={(event) => updateBuilder('valueField', event.target.value)}
                      >
                        {previewFields.map(field => (
                          <MenuItem key={field} value={field}>
                            {field}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="visualisation-series-label">Series field</InputLabel>
                      <Select
                        labelId="visualisation-series-label"
                        label="Series field"
                        value={builder.seriesField}
                        disabled={previewLoading || previewFields.length === 0}
                        onChange={(event) => updateBuilder('seriesField', event.target.value)}
                      >
                        <MenuItem value="">None</MenuItem>
                        {previewFields.map(field => (
                          <MenuItem key={field} value={field}>
                            {field}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="visualisation-aggregation-label">Aggregation</InputLabel>
                      <Select
                        labelId="visualisation-aggregation-label"
                        label="Aggregation"
                        value={builder.aggregation}
                        onChange={(event) =>
                          updateBuilder(
                            'aggregation',
                            event.target.value as NonNullable<VisualisationChartBuilderRequest['aggregation']>
                          )
                        }
                      >
                        {AGGREGATIONS.map(aggregation => (
                          <MenuItem key={aggregation.value} value={aggregation.value}>
                            {aggregation.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={9}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                      <Button
                        variant="contained"
                        onClick={handleBuildChart}
                        disabled={buildSubmitting || dataObjectsLoading || previewLoading || !isTabularCompatible}
                      >
                        {buildSubmitting ? 'Building...' : 'Build chart'}
                      </Button>
                      {selectedDataObject && (
                        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                          Source: {selectedDataObject.name} ({selectedDataObject.fileType})
                        </Typography>
                      )}
                    </Stack>
                  </Grid>
                </Grid>

                {previewLoading && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">Loading preview schema</Typography>
                  </Stack>
                )}
                {!previewLoading && previewFields.length > 0 && (
                  <Stack spacing={1}>
                    <Typography variant="subtitle2">Preview fields</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {previewFields.map(field => (
                        <Chip key={field} label={field} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </Paper>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Alert severity="info" data-testid="chart-builder-unavailable">
              Creating charts in Visualisation Studio is not available yet. Existing charts stay
              listed below, but they cannot be shown on dashboards yet.
            </Alert>
          </Grid>
        )}

        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Artifacts</Typography>
                <Chip label={`${artifacts.length}`} size="small" />
              </Stack>
              {artifactsLoading && (
                <Stack alignItems="center" sx={{ py: 2 }}>
                  <CircularProgress size={24} />
                </Stack>
              )}
              {artifactsError && <Alert severity="warning">{artifactsError}</Alert>}
              {!artifactsLoading && !artifactsError && artifacts.length === 0 && (
                <Alert severity="info">No visualisation artifacts are available for this tenant.</Alert>
              )}
              {!artifactsLoading && artifacts.length > 0 && (
                <List disablePadding>
                  {artifacts.map(artifact => (
                    <ListItemButton
                      key={artifact.id}
                      selected={artifact.id === selectedArtifactId}
                      onClick={() => setSelectedArtifactId(artifact.id)}
                      sx={{
                        border: 1,
                        borderColor: artifact.id === selectedArtifactId ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        mb: 1,
                        alignItems: 'flex-start',
                      }}
                    >
                      <ListItemText
                        disableTypography
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="subtitle1" component="span">
                              {artifact.name}
                            </Typography>
                            <Chip label={artifact.state} size="small" color={stateColor(artifact.state)} />
                          </Stack>
                        }
                        secondary={
                          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              {artifactTypeLabel(artifact.artifactType)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Updated {formatDateTime(artifact.updatedAt)}
                            </Typography>
                          </Stack>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2.5, minHeight: 360 }}>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                <Box>
                  <Typography variant="h6">Artifact detail</Typography>
                  {detailArtifact && (
                    <Typography variant="body2" color="text.secondary">
                      {artifactTypeLabel(detailArtifact.artifactType)}
                    </Typography>
                  )}
                </Box>
                {detailArtifact && (
                  <Chip label={detailArtifact.state} size="small" color={stateColor(detailArtifact.state)} />
                )}
              </Stack>
              <Divider />

              {detailLoading && (
                <Stack alignItems="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} />
                </Stack>
              )}
              {detailError && <Alert severity="warning">{detailError}</Alert>}
              {!detailLoading && !detailError && !detailArtifact && (
                <Alert severity="info">Select an artifact to view its detail.</Alert>
              )}

              {!detailLoading && !detailError && detailArtifact && (
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="h5" component="h2" sx={{ overflowWrap: 'anywhere' }}>
                      {detailArtifact.name}
                    </Typography>
                    {detailArtifact.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {detailArtifact.description}
                      </Typography>
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <DetailRow label="Owner" value={detailArtifact.ownerUserId || 'Not recorded'} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailRow label="Schema" value={detailArtifact.artifactSchemaVersion} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailRow label="Created" value={formatDateTime(detailArtifact.createdAt)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailRow label="Updated" value={formatDateTime(detailArtifact.updatedAt)} />
                    </Grid>
                  </Grid>

                  <Divider />
                  <Stack spacing={1}>
                    <Typography variant="subtitle2">Source references</Typography>
                    {sourceReferences(detailArtifact.sourceReferences).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        None
                      </Typography>
                    ) : (
                      <Stack spacing={1}>
                        {sourceReferences(detailArtifact.sourceReferences).map((reference, index) => (
                          <Stack key={`${reference.type || 'source'}-${reference.id || index}`} direction="row" spacing={1} flexWrap="wrap">
                            <Chip label={reference.type || 'source'} size="small" />
                            {reference.id && <Chip label={reference.id} size="small" variant="outlined" />}
                          </Stack>
                        ))}
                      </Stack>
                    )}
                  </Stack>

                  <Divider />
                  <Stack spacing={1}>
                    <Typography variant="subtitle2">Payload summary</Typography>
                    <Grid container spacing={1.5}>
                      {payloadSummary(detailArtifact).map(([label, value]) => (
                        <Grid item xs={12} sm={6} key={label}>
                          <DetailRow label={label} value={value} />
                        </Grid>
                      ))}
                    </Grid>
                  </Stack>
                </Stack>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Visualiser;
